import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const CartContext = createContext();

export const CartProvider = ({ children, user }) => {
  const [cartItems, setCartItems] = useState([]);
  const [currentCycleSummary, setCurrentCycleSummary] = useState({
    current_cycle_total: 0,
    credit_limit: 2500,
    remaining_credit: 2500,
    orders_count: 0,
    next_billing_date: null
  });

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Load current cycle summary
  const loadCurrentCycle = async () => {
    if (!user) return;

    try {
      const response = await fetch(`${getBackendUrl()}/api/orders/current-cycle`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentCycleSummary(data);
      }
    } catch (error) {
      console.error('Load current cycle error:', error);
    }
  };

  // Add item to cart
  const addToCart = (product, quantity) => {
    if (!user) {
      toast.error('Please log in to add items to cart');
      return false;
    }

    const existingItem = cartItems.find(item => item.product_id === product.id);

    if (existingItem) {
      // Update quantity
      setCartItems(cartItems.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + quantity, total_price: (item.quantity + quantity) * item.unit_price }
          : item
      ));
      toast.success(`Updated ${product.name} quantity`);
    } else {
      // Add new item
      const pricing = require('../pricingData').getPricingInfo(product.id);
      const unitPrice = pricing?.apsPrice || product.inventory?.pricePerUnit * product.inventory?.unitsPerCase || 0;

      const newItem = {
        id: `cart-${Date.now()}-${Math.random()}`,
        product_id: product.id,
        product_name: product.name,
        quantity: quantity,
        unit_price: unitPrice,
        total_price: unitPrice * quantity
      };

      setCartItems([...cartItems, newItem]);
      toast.success(`Added ${product.name} to cart`);
    }

    return true;
  };

  // Update quantity
  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(itemId);
      return;
    }

    setCartItems(cartItems.map(item =>
      item.id === itemId
        ? { ...item, quantity: newQuantity, total_price: newQuantity * item.unit_price }
        : item
    ));
  };

  // Remove from cart
  const removeFromCart = (itemId) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
    toast.success('Item removed from cart');
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
  };

  // Place order
  const placeOrder = async (orderNotes) => {
    if (!user) {
      toast.error('Please log in to place an order');
      return false;
    }

    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return false;
    }

    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${backendUrl}/api/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: cartItems.map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            quantity: item.quantity,
            unit_price: item.unit_price
          })),
          notes: orderNotes || ''
        })
      });

      if (response.ok) {
        const result = await response.json();
        clearCart();
        await loadCurrentCycle();
        return result;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to place order');
      }
    } catch (error) {
      console.error('Place order error:', error);
      throw error;
    }
  };

  // Load cycle summary when user changes
  useEffect(() => {
    if (user) {
      loadCurrentCycle();
    }
  }, [user]);

  const contextValue = {
    items: cartItems,
    periodSummary: currentCycleSummary,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    placeOrder,
    loadCurrentCycle,
    itemCount: cartItems.reduce((sum, item) => sum + item.quantity, 0),
    subtotal: cartItems.reduce((sum, item) => sum + item.total_price, 0)
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
